/**
 * Order domain service (Phase 4).
 *
 * Plain, framework-free helpers extracted out of the StandardOrder.vue god
 * component. Everything here is deliberately pure (data in, data out) and never
 * reads Vuex or `this`, so it is unit-testable in isolation and reusable from
 * any view.
 *
 * What lives here:
 *   - createBaseOrderItem  : the price/identity/metadata half of buildOrderItem
 *                            (tax calc stays in the component because it depends
 *                            on the tax mixins).
 *   - item grouping        : modifier signature + group-matching predicate
 *                            (the "group_qty" line-merge feature).
 *   - applyDiningOption    : the 4x-duplicated default + TABLE_SERVICE override
 *                            dining-option block.
 *
 * Money math reuses the single pricing engine where relevant; this module does
 * not reintroduce inline pricing formulas.
 */

import { toFixed } from "@/helpers/utils";
import { isTerminalSettingEnabled } from "@/helpers/terminal/settings";

const DINE_IN_MATCH = (option) =>
  !!(option &&
    option.name &&
    option.name.toLowerCase().includes("dine") &&
    option.name.toLowerCase().includes("in"));

/**
 * Build the base order-item object from a product + options. This mirrors steps
 * 1–5 of the legacy `buildOrderItem` (price resolution incl. special prices,
 * id/metadata, refund specifics). Tax fields are initialised to empty/zero; the
 * caller is responsible for running the tax mixins afterwards (steps 6–7).
 *
 * Pure: all environmental data (customer, employeeId, terminalId, localId,
 * createdAt) is passed in.
 *
 * @param {object} product
 * @param {object} options
 * @param {number} [options.quantity=1]
 * @param {boolean} [options.isRefund=false]
 * @param {number|null} [options.price=null] manual price override
 * @param {object|null} [options.customer] currentOrder.customer
 * @param {number|string|null} [options.employeeId]
 * @param {number|string|null} [options.terminalId]
 * @param {string|number} options.localId pre-generated unique item id
 * @param {string} [options.createdAt] ISO timestamp (defaults to now)
 * @returns {object} base order item
 */
export function createBaseOrderItem(product, options = {}) {
  const quantity = options.quantity !== undefined ? options.quantity : 1;
  const isRefund = options.isRefund || false;
  const priceOverride = options.price !== undefined ? options.price : null;
  const customer = options.customer || null;
  const localId = options.localId;
  const createdAt = options.createdAt || new Date().toISOString();

  // 2. Determine Price
  let itemPrice =
    priceOverride !== null
      ? parseFloat(toFixed(priceOverride, 2))
      : parseFloat(toFixed(product.refundedPrice || product.price, 2));

  // Handle Special Prices (Customer Groups) when no manual override provided.
  if (
    priceOverride === null &&
    product.special_prices_items &&
    product.special_prices_items.length &&
    customer &&
    customer.group
  ) {
    product.special_prices_items.forEach((specialPrice) => {
      if (specialPrice.group === customer.group) {
        itemPrice = specialPrice.price;
      }
    });
  }

  const newItem = {
    product: product.id,
    name: product.name,
    quantity: quantity,

    // Pricing
    price: itemPrice,
    price_base: itemPrice,
    price_before_tax: itemPrice, // Will be recalculated if tax inclusive
    subtotal: itemPrice * quantity,
    grand_total: itemPrice * quantity,
    cost: product.cost || 0,

    // Identifiers
    id: localId,
    local_id: localId,

    // Discounts & Modifiers
    discount: product.refundedDiscount || 0,
    modifiers: [],
    notes: "",

    // Cash discount: stored price is the plain list price until
    // calculateItemCashDiscountChange converts it (idempotency marker).
    cd_adjusted: false,

    // Status & Metadata
    void_status: 0,
    employee: options.employeeId !== undefined ? options.employeeId : null,
    terminal_id: options.terminalId,
    created_at: createdAt,

    // Tax & Kitchen
    tax_rate_value: product.tax_rate_value,
    tax_status: product.tax_rate_value ? 0 : 1, // Default; refund may override
    tax: 0,
    tax_items: [],

    // Printers
    printers: product.printers || [],
    has_kitchen_updates: !!(product.printers && product.printers.length),
    sent_to_kitchen: false,

    // Misc
    special_prices_items: product.special_prices_items,
    default_price: product.price,
    wholeDiscount: 0,
    discountPropotion: 0,

    // Discount eligibility: undefined/null (e.g. a product cached before this
    // field existed) defaults to allowed; only an explicit false blocks it.
    allow_discounts: product.allow_discounts !== false,
  };

  if (isRefund) {
    newItem.order_refund_item = product.refundItemId;
    newItem.tax_status = product.refundItemTax_status;
    newItem.void_item = 0;
  }

  return newItem;
}

// ─── Item grouping (group_qty line merge) ─────────────────────────────────────

/**
 * Stable signature of an item's active modifiers, used to decide whether two
 * lines can be merged. Pure.
 */
export function getItemModifierSignature(item) {
  const modifiers = Array.isArray(item && item.modifiers) ? item.modifiers : [];
  return JSON.stringify(
    modifiers
      .filter((mod) => mod && !mod.void_status)
      .map((mod) => ({
        group: mod.group || null,
        oryginal: mod.oryginal || null,
        price: Number(mod.price || 0),
      }))
      .sort((a, b) => {
        if (a.group !== b.group) return String(a.group).localeCompare(String(b.group));
        return String(a.oryginal).localeCompare(String(b.oryginal));
      })
  );
}

/**
 * Comparable unit price for grouping. Under cash-discount mode the stored
 * `price` is mutated, so the menu `default_price` is the stable key.
 */
export function getGroupableItemPrice(item, { cashDiscount = false } = {}) {
  if (cashDiscount && item && item.default_price !== undefined) {
    return Number(item.default_price || 0);
  }
  return Number((item && item.price) || 0);
}

/**
 * An item's cashier-applied discount, ignoring any promotion-driven discount.
 * Promotion discounts are recomputed for the whole cart on every add (see
 * reEvaluatePromotions), so a freshly-added line will always start at 0 while
 * an existing line may already carry a stale, non-zero promo discount from a
 * prior recompute. Comparing raw `discount` would treat that mismatch as a
 * "different line" and block merging every time a promo is active; comparing
 * only the manual portion lets grouping proceed and leaves the promo engine
 * to reconcile the merged line's discount right after.
 */
function getManualDiscount(item) {
  if (!item) return 0;
  if (item.isPromotionItem || item.promotion_applied) return 0;
  return Number(item.discount || 0);
}

/**
 * Whether two order lines may be merged into one (qty bumped). Pure: the
 * group_qty flag and cash-discount mode are passed in.
 */
export function canGroupOrderItems(existingItem, newItem, options = {}) {
  const { groupQtyEnabled = false, cashDiscount = false } = options;
  if (!groupQtyEnabled) return false;
  if (!existingItem || !newItem) return false;
  if (existingItem.void_status || newItem.void_status) return false;
  if (existingItem.sent_to_kitchen || newItem.sent_to_kitchen) return false;
  if (existingItem.product === -2 || newItem.product === -2) return false;
  if (existingItem.order_refund_item || newItem.order_refund_item) return false;

  return (
    existingItem.product === newItem.product &&
    getGroupableItemPrice(existingItem, { cashDiscount }) ===
      getGroupableItemPrice(newItem, { cashDiscount }) &&
    getManualDiscount(existingItem) === getManualDiscount(newItem) &&
    (existingItem.notes || "") === (newItem.notes || "") &&
    Number(existingItem.tax_status || 0) === Number(newItem.tax_status || 0) &&
    getItemModifierSignature(existingItem) === getItemModifierSignature(newItem)
  );
}

/**
 * Index of an existing line that `newItem` can merge into, or -1. Pure.
 */
export function findGroupableOrderItemIndex(items, newItem, options = {}) {
  const { excludeIndex = -1 } = options;
  if (!Array.isArray(items)) return -1;
  return items.findIndex(
    (item, index) => index !== excludeIndex && canGroupOrderItems(item, newItem, options)
  );
}

// ─── Item display name ────────────────────────────────────────────────────────

/**
 * Resolve the name shown for an order line. When the terminal's
 * `kitchen_name_pos` setting is enabled and the product carries a
 * `kitchen_display_name`, that name is used; otherwise the line's own name.
 *
 * Collapses the copy that lived in OrdersList.vue and StandardOrderCheckout.vue.
 * Pure: terminal + products are passed in (never read from Vuex / `this`).
 *
 * @param {object} item    order line item
 * @param {object} terminal
 * @param {Array}  products config products
 * @returns {string}
 */
export function resolveItemDisplayName(item, terminal, products) {
  if (!item) return "";
  if (isTerminalSettingEnabled(terminal, "kitchen_name_pos") && item.product) {
    const list = Array.isArray(products) ? products : [];
    const product = list.find((p) => p.id === item.product);
    if (product && product.kitchen_display_name) {
      return product.kitchen_display_name;
    }
  }
  return item.name;
}

/**
 * Resolve the name shown for a PRODUCT (grid tile / info). When kitchen_name_pos
 * is enabled and the product has a `kitchen_display_name`, that name is used.
 * Mirrors StandardOrder.getProductDisplayName exactly (no extra null guard).
 *
 * @param {object} product
 * @param {object} terminal
 * @returns {string}
 */
export function resolveProductDisplayName(product, terminal) {
  if (isTerminalSettingEnabled(terminal, "kitchen_name_pos")) {
    if (product && product.kitchen_display_name) {
      return product.kitchen_display_name;
    }
  }
  return product.name;
}

/**
 * Resolve the name shown for a product in SEARCH results. When kitchen_name_pos
 * is enabled and the product has a `kitchen_display_name`, the combined
 * "(kitchen) name" form is used. Mirrors StandardOrder.getSearchProductDisplayName.
 *
 * @param {object} product
 * @param {object} terminal
 * @returns {string}
 */
export function resolveSearchProductDisplayName(product, terminal) {
  if (isTerminalSettingEnabled(terminal, "kitchen_name_pos")) {
    if (product && product.kitchen_display_name) {
      return `(${product.kitchen_display_name}) ${product.name}`;
    }
  }
  return product.name;
}

// ─── Dining option ────────────────────────────────────────────────────────────

/**
 * Apply the default dining option and the TABLE_SERVICE "Dine-in" override to an
 * order, in place. Collapses the block that was duplicated 4x in
 * StandardOrder.vue (init / createNewOrder / two new-order branches).
 *
 * @param {object} order            currentOrder (mutated)
 * @param {object} opts
 * @param {Array}  opts.diningOptions       terminal.dining_options
 * @param {*}      opts.defaultDiningOption  store/route default option id
 * @param {boolean} opts.isTableService      terminalMode === "TABLE_SERVICE"
 * @param {boolean} [opts.onlyIfUnset=true]  apply default only when no option is
 *   set yet. `createNewOrder` historically applied unconditionally → pass false.
 * @returns {object} the same order
 */
export function applyDiningOption(order, opts = {}) {
  const { diningOptions, defaultDiningOption, isTableService, onlyIfUnset = true } = opts;
  if (!order) return order;

  const shouldApplyDefault = onlyIfUnset
    ? !order.dining_option_type && defaultDiningOption
    : !!defaultDiningOption;

  if (shouldApplyDefault) {
    order.dining_option_type = defaultDiningOption;
    const found = (diningOptions || []).find(
      (option) => order.dining_option_type === option.id
    );
    order.dining_option = found ? found.name : undefined;
  }

  if (isTableService && diningOptions) {
    const dineInOption = diningOptions.find(DINE_IN_MATCH);
    if (dineInOption) {
      order.dining_option_type = dineInOption.id;
      order.dining_option = dineInOption.name;
    }
  }

  return order;
}
